import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import cx from 'classnames';
import AnimatedHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { highlightText } from '@app/utils';
import { DiseaseV2ViewChild } from '@data/sdk/match';
import {
  AntdCheckbox,
  AntdGrid,
  AntdInput,
  AntdModal,
  AntdSpin,
  AntdTag,
  AntdTypography,
  Button,
} from '@ui/components';
import { EmptyState, EndOfResults } from './components';
import { useDiseasesSearch } from './hooks';

const { Text } = AntdTypography;
const { useBreakpoint } = AntdGrid;

export type DiseaseOptionV2 = {
  icdCode: string;
  name: string;
};

export type DiseaseHierarchyOptionV2 = {
  children: DiseaseV2ViewChild[];
  icdCode: string;
  id: string;
  name?: string;
  names: string[];
};

type DiseaseSearchProps = {
  allowHealthy?: boolean;
  autoFocus?: boolean;
  enabledAnalytics?: boolean;
  fakeInputId?: string;
  fullScreen?: boolean;
  inputId?: string;
  isPrefetchingHierarchy?: boolean;
  onChange?: (diseases: DiseaseOptionV2[]) => void;
  prefetchedHierarchy?: DiseaseHierarchyOptionV2[];
  value?: DiseaseOptionV2[];
};

export const DiseaseSearchV2 = ({
  value,
  onChange,
  fakeInputId,
  inputId,
  autoFocus = false,
  fullScreen = false,
  allowHealthy = false,
  enabledAnalytics = false,
  prefetchedHierarchy = [],
  isPrefetchingHierarchy = false,
}: DiseaseSearchProps) => {
  const { t } = useTranslation();
  const { md } = useBreakpoint();
  const {
    inputRef,
    diseases,
    hasNextPage,
    queryEnabled,
    showEmptyState,
    searchString,
    hasDraftChanges,
    selectedTagOptions,
    draftTagOptions,
    isDiseasesSearchOpen,
    isInitialLoading,
    isFetchingMore,
    fetchNextPage,
    handleSelectDisease,
    isDiseaseChecked,
    isParentIndeterminate,
    handleSearchChange,
    openDiseasesSearch,
    closeDiseasesSearchWithoutApply,
    applyDiseasesSelection,
    handleRemoveDraftTag,
    handleRemoveSelectedTagImmediately,
  } = useDiseasesSearch(
    value || [],
    onChange || (() => {}),
    autoFocus,
    allowHealthy,
    enabledAnalytics,
    prefetchedHierarchy,
  );

  const shouldShowApplyButton = !showEmptyState && hasDraftChanges;
  const shouldShowSelectedTagsLoader = isPrefetchingHierarchy && (value?.length || 0) > 0;

  return (
    <>
      <Button
        size="large"
        id={fakeInputId}
        aria-label={fakeInputId}
        onClick={openDiseasesSearch}
        className="w-full h-10 flex items-center justify-between px-3"
        data-testid="open-disease-search"
      >
        <Text className="overflow-hidden text-[#bfbfbf]">
          {t('sign_up_v2_page.disease_form.disease_search_v2.toggle_button')}
        </Text>
        <SearchOutlined className="text-base-500" />
      </Button>
      <AnimatedHeight
        height={shouldShowSelectedTagsLoader || selectedTagOptions.length > 0 ? 'auto' : 0}
      >
        {shouldShowSelectedTagsLoader ? (
          <div className="flex items-center justify-center mt-1 min-h-[30px]">
            <AntdSpin size="small" />
          </div>
        ) : (
          selectedTagOptions.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3 max-h-[150px] overflow-y-auto">
              {selectedTagOptions.map((tag) => (
                <AntdTag
                  key={tag.icdCode}
                  className={cx(
                    'bg-gray-100 rounded-md px-2 py-0 whitespace-normal break-words m-0',
                    tag.type === 'parent' && 'bg-primary-600 text-white border-primary-600',
                  )}
                  closable
                  closeIcon={
                    <CloseOutlined
                      className={cx(tag.type === 'parent' ? 'text-white' : 'text-base-500')}
                    />
                  }
                  onClose={() => handleRemoveSelectedTagImmediately(tag)}
                >
                  {tag.name}
                </AntdTag>
              ))}
            </div>
          )
        )}
      </AnimatedHeight>

      <AntdModal
        title={null}
        closeIcon={null}
        preventScrollOnMobile
        destroyOnHidden
        open={isDiseasesSearchOpen}
        onCancel={closeDiseasesSearchWithoutApply}
        footer={null}
        width={md && !fullScreen ? '90vw' : '100vw'}
        className={cx(
          'md:max-w-screen-2xl',
          !md ? '!top-0 !p-0 !m-0 !w-full !h-[100dvh] !max-w-none !pb-0' : '',
          fullScreen && '!top-0 !p-0 !m-0 !w-full !h-[100dvh] !max-w-none !pb-0',
        )}
        classNames={{
          body: 'p-0 flex-1 min-h-0 overflow-hidden',
          container: cx(
            'rounded-none md:rounded-3xl p-0 overflow-hidden flex flex-col',
            fullScreen
              ? 'h-[100dvh] md:h-[100dvh] max-h-[100dvh] rounded-none !rounded-none'
              : 'h-[100dvh] md:h-[80dvh] md:max-h-[80dvh]',
          ),
          wrapper: cx('h-screen overflow-hidden', fullScreen && 'h-[100dvh]'),
          header: 'hidden',
        }}
      >
        <div className="relative flex h-full min-h-0 flex-col">
          {/* Header */}
          <div className="bg-white px-4 md:px-6 pt-5 pb-5 border-b border-base-200 flex-shrink-0">
            <div className="flex items-center justify-between mb-4">
              <Text className="text-xl md:text-2xl font-semibold text-base-800">
                {t('sign_up_v2_page.disease_form.disease_search_v2.title')}
              </Text>
              <button
                type="button"
                onClick={closeDiseasesSearchWithoutApply}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-base-200 hover:bg-base-300 transition-colors cursor-pointer border-0"
                data-testid="close-disease-search-modal"
              >
                <CloseOutlined className="text-base-600 text-sm" />
              </button>
            </div>

            <AntdInput
              placeholder={t('sign_up_v2_page.disease_form.disease_search_v2.input_placeholder')}
              size="large"
              id={inputId}
              aria-label={inputId}
              suffix={<SearchOutlined className="text-base-400" />}
              value={searchString}
              ref={inputRef}
              autoFocus={autoFocus}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="rounded-xl"
              data-testid="disease-search-input"
            />

            <AnimatedHeight height={draftTagOptions.length > 0 ? 'auto' : 0}>
              {draftTagOptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4 max-h-[100px] overflow-y-auto">
                  {draftTagOptions.map((tag) => (
                    <AntdTag
                      key={tag.icdCode}
                      className={cx(
                        'bg-primary-100 rounded-lg px-2.5 py-0.5 whitespace-normal break-words m-0 border border-primary-200 text-primary-700 font-medium',
                        tag.type === 'parent' && 'bg-primary-600 text-white border-primary-600',
                      )}
                      closable
                      closeIcon={
                        <CloseOutlined
                          className={cx(tag.type === 'parent' ? 'text-white' : 'text-primary-700')}
                        />
                      }
                      onClose={() => handleRemoveDraftTag(tag)}
                    >
                      {tag.name}
                    </AntdTag>
                  ))}
                </div>
              )}
            </AnimatedHeight>
          </div>

          {showEmptyState ? (
            <div className="flex-1 min-h-0 overflow-y-auto bg-gradient-to-b from-base-100 to-base-200/30">
              <EmptyState
                enabledAnalytics={enabledAnalytics}
                queryEnabled={queryEnabled}
                searchString={searchString}
              />
            </div>
          ) : (
            <div className="flex-1 min-h-0 px-4 md:px-6 overflow-y-auto bg-gradient-to-b from-base-100 to-base-200/30">
              {isInitialLoading && (
                <div className="flex items-center justify-center py-12">
                  <AntdSpin size="large" />
                </div>
              )}
              <ul className="list-none p-0 pt-4">
                {diseases.map(({ id, icdCode, name, names, children }, index) => (
                  <li
                    key={`disease-${id}`}
                    className="mt-1 first:mt-0 w-full rounded-xl hover:bg-primary-100/50 transition-colors px-3 py-2"
                  >
                    <AntdCheckbox
                      checked={isDiseaseChecked(icdCode)}
                      indeterminate={isParentIndeterminate(icdCode, children)}
                      data-testid={`disease-${index}`}
                      onChange={() => handleSelectDisease(icdCode, name || icdCode)}
                    >
                      <Text className="text-base-900">
                        {highlightText(name || icdCode, `disease-${id}`, searchString)}{' '}
                        {names?.length > 0 && (
                          <span className="text-base-800">
                            ({highlightText(names.join(', '), `disease-${id}-names`, searchString)})
                          </span>
                        )}
                      </Text>
                    </AntdCheckbox>

                    {children.length > 0 && (
                      <ul className="list-none p-0 pl-6 mt-1 border-l-2 border-primary-200 ml-2">
                        {children.map((c) => (
                          <li
                            key={`disease-${c.id}`}
                            className="mt-1 first:mt-0 rounded-lg hover:bg-primary-100/30 transition-colors px-2 py-1.5"
                          >
                            <AntdCheckbox
                              checked={isDiseaseChecked(c.icdCode, icdCode)}
                              className="w-full"
                              onChange={() =>
                                handleSelectDisease(c.icdCode, c.name, icdCode, name, children)
                              }
                            >
                              <Text className="text-base-700 text-sm">
                                {highlightText(
                                  c.name || c.icdCode,
                                  `disease-${c.id}`,
                                  searchString,
                                )}{' '}
                                {c.names?.length > 0 && (
                                  <span className="text-base-600">
                                    (
                                    {highlightText(
                                      c.names.join(', '),
                                      `disease-${c.id}-names`,
                                      searchString,
                                    )}
                                    )
                                  </span>
                                )}
                              </Text>
                            </AntdCheckbox>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
              {!isInitialLoading && (
                <EndOfResults
                  enabledAnalytics={enabledAnalytics}
                  hasNextPage={!!hasNextPage}
                  isFetchingMore={isFetchingMore}
                  searchString={searchString}
                  onLoadMore={fetchNextPage}
                />
              )}
            </div>
          )}

          <div
            className={cx(
              'absolute left-0 right-0 bottom-0 px-4 md:px-6 pb-4 flex justify-center transition-all duration-300 ease-out',
              shouldShowApplyButton
                ? 'opacity-100 translate-y-0 pointer-events-auto'
                : 'opacity-0 translate-y-2 pointer-events-none',
            )}
          >
            <Button
              size="large"
              type="primary"
              data-testid="apply-diseases-selection"
              onClick={applyDiseasesSelection}
              className="w-full max-w-xs px-10 py-5 opacity-80 hover:opacity-100 transition-opacity duration-200 text-base"
            >
              {t('sign_up_v2_page.disease_form.disease_search_v2.apply', {
                count: draftTagOptions.length,
              })}
            </Button>
          </div>
        </div>
      </AntdModal>
    </>
  );
};
